import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  model,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ContentActionBarMainItem,
  SiContentActionBarComponent
} from '@siemens/element-ng/content-action-bar';
import { SiEmptyStateModule } from '@siemens/element-ng/empty-state';
import { SiFormModule } from '@siemens/element-ng/form';
import { SiModalService } from '@siemens/element-ng/modal';
import { SiTooltipDirective } from '@siemens/element-ng/tooltip';
import { Track, TrackDataSet } from 'src/app/main/shared/DataTypes';
import { Waypoint } from 'src/app/main/shared/brouter-map/models/brouter-map.model';
import { EventService } from 'src/app/main/shared/services/event.service';

import { TrackParameterName, TrackService } from '../track.service';
import { TrackCommentComponent } from './track-comment/track-comment.component';
import { TrackMapComponent } from './track-map/track-map.component';
import { TrackPlotComponent } from './track-plot/track-plot.component';

@Component({
  selector: 'app-track-table',
  imports: [
    TranslateModule,
    SiFormModule,
    ReactiveFormsModule,
    FormsModule,
    SiEmptyStateModule,
    TrackPlotComponent,
    TrackCommentComponent,
    TrackMapComponent,
    CommonModule,
    SiContentActionBarComponent,
    SiTooltipDirective
  ],
  templateUrl: './track-table.component.html',
  styleUrl: './track-table.component.scss'
})
export class TrackTableComponent implements OnInit {
  eventService = inject(EventService);
  trackService = inject(TrackService);
  formBuilderService = inject(FormBuilder);
  modalService = inject(SiModalService);
  translateService = inject(TranslateService);
  readonly selectedTrack = model.required<Track | undefined>();
  readonly buttonsDisabledChanged = output<boolean>();
  readonly editRouteRequested = output<Waypoint[]>();
  readonly trackPlotRef = viewChild<TrackPlotComponent>('trackPlotRef');

  constructor() {
    // EARLY TRACK CHANGE WATCHER
    // Clears all stored row change state immediately when a completely new track (new parameters root ref) is provided.
    // Uses parameters object reference to distinguish external track change from internal edits (which keep the same ref).
    let previousParametersRef: any = null;
    effect(() => {
      const track = this.selectedTrack();
      const currentParametersRef = track?.parameters;
      if (currentParametersRef && currentParametersRef !== previousParametersRef) {
        this.clearAllStoredChanges();
      }
      previousParametersRef = currentParametersRef;
    });

    effect(() => {
      // Update form when selectedDataSet changes
      if (this.trackParameterKeys()) {
        this.buildNewEntryForm();
        this.validateAllRows();
      }
    });

    effect(() => {
      // Emit event when hasInvalidRows changes
      this.buttonsDisabledChanged.emit(this.buttonsDisabled());
    }); // Track parameter changes - no longer clear stored changes when switching datasets
    // to maintain persistent multi-row state management
    let previousParameterName: TrackParameterName | null = null;
    effect(() => {
      const currentParameterName = this.selectedParameterName();
      if (previousParameterName !== null && previousParameterName !== currentParameterName) {
        // State persists across parameter navigation - no clearing needed
      }
      previousParameterName = currentParameterName;
    });
  }

  ngOnInit(): void {
    this.eventService.openNewTrackEvent$.subscribe(() => {
      this.selectedParameterName.set('station_data');
    });
  }

  readonly selectedDataSet = computed(
    () => this.selectedTrack()?.parameters![this.selectedParameterName()]
  );
  // Column Names
  readonly trackParameterKeys = computed(() => {
    return this.selectedParameterName()
      ? Object.keys(this.trackService.parameterNameMappings[this.selectedParameterName()])
      : [];
  });

  readonly parameterNames = signal<string[]>(this.trackService.parameterNames);
  readonly selectedParameterName = signal<TrackParameterName>('station_data');

  readonly editableRowIndex = signal<number | null>(null);
  readonly rowValidationStatus = signal<{ [key: number]: string | null }>({});
  // Checks if there is at least one invalid row
  readonly hasInvalidRows = computed(() => {
    const validationStatus = Object.values(this.rowValidationStatus());
    return validationStatus.some(status => status !== null);
  });
  newRowInfoMessage: string | null = null;
  newRowDataForm = new FormGroup({});

  // PERSISTENT MULTI-ROW STATE MANAGEMENT
  // ====================================
  // Key Principles:
  // - Never clear change tracking when switching rows
  // - Maintain parallel tracking for all edited rows
  // - Initialize tracking only once per row (when first edited)
  // - Preserve state across navigation

  // Multi-row change tracking signals - persistent across navigation
  readonly originalRowDataMap = signal<Map<string, TrackDataSet>>(new Map());
  readonly changesSummaryMap = signal<Map<string, string>>(new Map());
  readonly editedRowsSet = signal<Set<string>>(new Set());
  // Legacy computed properties for backward compatibility with TrackCommentComponent
  readonly originalRowData = computed(() => {
    const currentRowKey = this.getCurrentRowKey();
    return currentRowKey ? (this.originalRowDataMap().get(currentRowKey) ?? null) : null;
  });
  readonly changesSummary = computed(() => {
    const currentRowKey = this.getCurrentRowKey();
    return currentRowKey ? (this.changesSummaryMap().get(currentRowKey) ?? null) : null;
  });
  readonly changesForRowIndex = computed(() => {
    return this.editableRowIndex();
  });
  // Warning toggle with mandatory comment signals
  readonly pendingWarningToggleIndex = signal<number | null>(null);
  readonly forceCommentModalForWarning = signal<boolean>(false);
  readonly warningToggleContext = signal<{
    entryIndex: number;
    currentWarningState: boolean;
    newWarningState: boolean;
  } | null>(null);

  plotModalPrimaryActions: ContentActionBarMainItem[] = [
    { type: 'action', label: 'Toggle Fullscreen', action: () => this.toggleFullscreen() }
  ];
  toggleFullscreen() {
    const modalEl = document.querySelector('.modal-dialog');
    (modalEl as HTMLElement)?.classList.toggle('responsive-modal');
    (modalEl as HTMLElement)?.classList.toggle('fullscreen-modal');
  }

  // Helper method to generate unique row keys for persistent tracking
  private getCurrentRowKey(): string | null {
    const currentIndex = this.editableRowIndex();
    return currentIndex !== null ? `${this.selectedParameterName()}_${currentIndex}` : null;
  }

  private getRowKey(parameterName: TrackParameterName, rowIndex: number): string {
    return `${parameterName}_${rowIndex}`;
  }
  setEditableRow(index: number): void {
    const rowKey = this.getRowKey(this.selectedParameterName(), index);

    // Initialize tracking only once per row (when first edited)
    if (!this.editedRowsSet().has(rowKey)) {
      // Store original data before editing starts
      const originalData = { ...this.selectedDataSet()![index] };

      // Update the maps immutably
      this.originalRowDataMap.update(map => new Map(map).set(rowKey, originalData));
      this.editedRowsSet.update(set => new Set(set).add(rowKey));
    }

    this.editableRowIndex.set(index);
    this.updateRowErrors(index);
  }
  saveRow(index: number): void {
    // HTML Form data is string, convert to number
    this.selectedDataSet()![index] = this.convertRowDataToNumber(this.selectedDataSet()![index]);

    // Generate and store changes summary for this specific row
    const rowKey = this.getRowKey(this.selectedParameterName(), index);
    const originalData = this.originalRowDataMap().get(rowKey);

    if (originalData) {
      const changes = this.generateChangesSummary(originalData, this.selectedDataSet()![index]);
      if (changes) {
        this.changesSummaryMap.update(map => new Map(map).set(rowKey, changes));
      } else {
        // If there are no changes compared to original, clear any previously stored tracking for this row
        this.clearStoredChangesForRow(rowKey);
      }
    }

    this.editableRowIndex.set(null);
    this.updateRowErrors(index);

    // Recalculate gradient values if elevation data was edited via the table
    if (this.selectedParameterName() === 'elevation_data') {
      this.recalculateElevationGradients();
    }

    this.markTrackAsManuallyChanged();
  }
  removeEntry(index: any) {
    const rowKey = this.getRowKey(this.selectedParameterName(), index);

    // Remove tracking data for this specific row
    this.originalRowDataMap.update(map => {
      const newMap = new Map(map);
      newMap.delete(rowKey);
      return newMap;
    });
    this.changesSummaryMap.update(map => {
      const newMap = new Map(map);
      newMap.delete(rowKey);
      return newMap;
    });
    this.editedRowsSet.update(set => {
      const newSet = new Set(set);
      newSet.delete(rowKey);
      return newSet;
    });

    this.selectedTrack()!.parameters![this.selectedParameterName()].splice(index, 1);
    this.validateAllRows();
    this.markTrackAsManuallyChanged();
  }

  validateAllRows() {
    const allRowData = this.selectedDataSet();
    if (!allRowData || allRowData.length === 0) {
      this.rowValidationStatus.set({});
      return;
    }

    const newStatus: { [key: number]: string | null } = {};
    allRowData.forEach((_, index) => {
      newStatus[index] = this.getRowError(index);
    });
    this.rowValidationStatus.set(newStatus);
  }

  updateRowErrors(rowIndex: number) {
    if (this.selectedDataSet()!.length !== 0) {
      this.validateAllRows();
    }
  }

  getRowError(rowIndex: number): string | null {
    const rowData = this.selectedDataSet()![rowIndex];
    const allRowData = this.selectedDataSet()!;

    // Station validation
    if (this.selectedParameterName() == 'station_data') {
      // There must be at least 2 stations
      if (allRowData.length < 2) {
        return 'There must be at least 2 stations';
      }

      // The first station must be at position 0
      // Explicitly parsing to Number type needed to avoid type coercion
      if (Number(rowData.position) !== 0 && rowIndex === 0) {
        return 'First station must be at position 0';
      }
    }

    // No entry can be negative
    if ((rowData.position as number) < 0) {
      return 'Entry position cannot be negative';
    }

    // No entry can be less far down the track than the station before it
    if (rowIndex > 0 && rowData.position < allRowData[rowIndex - 1].position) {
      return 'Entry position must be greater than the previous position';
    }

    // No entry can be more far down the track than the station after it
    if (rowIndex < allRowData.length - 1 && rowData.position > allRowData[rowIndex + 1].position) {
      return 'Entry position must be lower than the next position';
    }

    return null;
  }

  // Check if row data is string and convert to number
  convertRowDataToNumber(row: TrackDataSet): TrackDataSet {
    // Convert inputs to numbers (ecxept for station names)
    for (const key in row) {
      if (!(key === 'trackParameter1' && this.selectedParameterName() === 'station_data')) {
        if (typeof row[key] === 'string') {
          row[key] = Number(row[key]);
        }
      }
    }
    return row;
  }

  addNewRow() {
    if (this.newRowDataForm.valid) {
      // Parse position to number to use in .find() and for backend
      const newRowPosition = Number(this.newRowDataForm.get('position')?.value);
      let newRowData: TrackDataSet = {
        ...this.newRowDataForm.value,
        position: newRowPosition,
        trackParameter1: this.newRowDataForm.get('trackParameter1')?.value ?? 0,
        comments: [],
        warning: false
      };
      newRowData = this.convertRowDataToNumber(newRowData);

      // Check if there's an existing row at the same position
      if (this.selectedDataSet()!.length === 0) {
        const existingRow = this.selectedDataSet()!.find(row => row.position === newRowPosition);

        if (existingRow) {
          // Handle the case where a row already exists at the same position
          this.newRowInfoMessage = `A row already exists at position ${newRowPosition}`;
          return;
        } else {
          this.newRowInfoMessage = null;
        }
      }

      // Find the index where the new row should be inserted
      const insertIndex = this.selectedDataSet()!.findIndex(
        row => (row.position as number) > newRowPosition
      );

      if (insertIndex === -1) {
        // If the new row should be added at the end of the array
        this.selectedDataSet()!.push(newRowData);
      } else {
        // Insert the new row at the correct position
        this.selectedDataSet()!.splice(insertIndex, 0, newRowData);
      }

      // Reset the form
      this.newRowDataForm.reset();

      this.validateAllRows();
      this.markTrackAsManuallyChanged();
    }
  }

  getDisplayNameForKeyName(key: string): string {
    return this.trackService.parameterNameMappings[this.selectedParameterName()][key].name;
  }

  buildNewEntryForm() {
    // Build form to add new Entry
    this.newRowDataForm = this.formBuilderService.group({});
    this.trackParameterKeys().forEach(key => {
      this.newRowDataForm.addControl(key, this.formBuilderService.control('', Validators.required));
    });
  }

  readonly atleastTwoStations = computed(() => {
    return this.selectedTrack()!.parameters!['station_data'].length >= 2;
  });

  readonly buttonsDisabled = computed(() => {
    if (this.editableRowIndex() !== null || this.hasInvalidRows() || !this.atleastTwoStations()) {
      return true;
    }
    return false;
  });

  markTrackAsManuallyChanged() {
    this.selectedTrack.set({
      ...this.selectedTrack()!,
      id: undefined,
      version: undefined
    });
  }
  reverseTrackParameters() {
    // Clear any pending changes as the indices will change
    this.clearAllStoredChanges();

    const track = this.selectedTrack()!;
    const reversedParameters: { [key: string]: TrackDataSet[] } = {};

    for (const parameterKey in track.parameters) {
      if (Object.prototype.hasOwnProperty.call(track.parameters, parameterKey)) {
        const dataSet = track.parameters[parameterKey];

        if (dataSet.length === 0) continue; // Skip empty datasets

        // Get the max position
        const maxPosition = Number(dataSet[dataSet.length - 1].position);

        // Reverse the dataset and adjust positions
        const reversedDataSet = dataSet.map((item, index) => {
          const reversedIndex = dataSet.length - 1 - index;
          return {
            ...dataSet[reversedIndex],
            position: index === 0 ? 0 : maxPosition - Number(dataSet[reversedIndex].position)
          };
        });

        reversedParameters[parameterKey] = reversedDataSet;
      }
    }

    // Reverse trackDataQuery waypoints if they exist
    let reversedTrackDataQuery = track.trackDataQuery;
    if (track.trackDataQuery?.waypoints && track.trackDataQuery.waypoints.length > 0) {
      reversedTrackDataQuery = {
        ...track.trackDataQuery,
        waypoints: [...track.trackDataQuery.waypoints].reverse()
      };
    }

    this.selectedTrack.set({
      ...track,
      parameters: reversedParameters,
      trackDataQuery: reversedTrackDataQuery
    });

    this.markTrackAsManuallyChanged();
  }
  openInScheduleClicked() {
    this.eventService.openTrackAsSchedule(this.selectedTrack()!);
  }

  openModal(template: TemplateRef<any>, inputValues: Record<string, any> = {}) {
    this.modalService.show(template, {
      inputValues,
      class: 'responsive-modal modal-dialog-centered'
    });
  }

  closeModal(modalRef: any): void {
    modalRef.hide();
  }

  onCommentAdded() {
    // Check if this comment was added due to mandatory warning toggle
    const pendingIndex = this.pendingWarningToggleIndex();
    if (pendingIndex !== null && this.forceCommentModalForWarning()) {
      // Complete the pending warning toggle now that comment is added
      this.performWarningToggle(pendingIndex);
      // DON'T clear edit changes for warning comments - they should remain for regular comments
    } else {
      // For regular comments, clear changes only for the specific row that was commented
      const currentRowKey = this.getCurrentRowKey();
      if (currentRowKey && this.changesSummaryMap().has(currentRowKey)) {
        this.clearStoredChangesForRow(currentRowKey);
      }
    }
  }
  onWarningCommentCanceled() {
    // User closed mandatory warning comment modal without adding a comment.
    // Clear pending warning toggle so user can attempt again later.
    this.pendingWarningToggleIndex.set(null);
    this.forceCommentModalForWarning.set(false);
    this.warningToggleContext.set(null);
  }

  // Handle comments added from the track-plot component
  onCommentAddedFromPlot(evt: {
    dataKey: string;
    index: number;
    comment: { date: string; content: string };
  }) {
    // Ensure the event corresponds to the currently selected parameter set
    if (evt.dataKey !== this.selectedParameterName()) {
      console.warn(
        'Comment event dataKey mismatch:',
        evt.dataKey,
        'vs',
        this.selectedParameterName()
      );
      return;
    }

    // Use the immutable update pattern to add the comment
    this.updateParameterEntry(evt.index, entry => {
      // Initialize comments array if it doesn't exist
      const existingComments = entry.comments ?? [];
      return {
        ...entry,
        comments: [...existingComments, evt.comment]
      };
    });

    console.debug(`Comment added to ${evt.dataKey} at index ${evt.index} via plot`);
  }

  toggleRowWarningStateClicked(entryIndex: number) {
    const currentEntry = this.selectedDataSet()![entryIndex];
    const currentWarningState = currentEntry.warning || false;
    const newWarningState = !currentWarningState;

    // ALWAYS require a comment for EVERY warning state change (ON or OFF)
    // Warning toggles are completely independent of any editing changes
    this.pendingWarningToggleIndex.set(entryIndex);
    this.forceCommentModalForWarning.set(true);
    this.warningToggleContext.set({
      entryIndex,
      currentWarningState,
      newWarningState
    });
  }
  private performWarningToggle(entryIndex: number) {
    if (this.selectedTrack()!.id) {
      this.trackService
        .toggleRowWarningState(this.selectedTrack()!.id!, this.selectedParameterName(), entryIndex)
        .subscribe({
          next: (data: any) => {
            this.updateParameterEntry(entryIndex, entry => ({
              ...entry,
              warning: data.warning
            }));
            this.clearPendingWarningToggle();
          }
        });
    } else {
      this.updateParameterEntry(entryIndex, entry => ({
        ...entry,
        warning: !entry.warning
      }));
      this.clearPendingWarningToggle();
    }
  }
  private clearPendingWarningToggle() {
    this.pendingWarningToggleIndex.set(null);
    this.forceCommentModalForWarning.set(false);
    this.warningToggleContext.set(null);
  }
  // Helper method to update a specific parameter entry
  private updateParameterEntry(entryIndex: number, updater: (entry: any) => any) {
    this.selectedTrack.update(track => {
      if (!track) return track;
      const parameters = { ...track.parameters };
      const paramArray = [...parameters[this.selectedParameterName()]];
      paramArray[entryIndex] = updater(paramArray[entryIndex]);
      parameters[this.selectedParameterName()] = paramArray;
      return { ...track, parameters };
    });
  }

  // Plot datapoint drag handler (emitted from <app-track-plot>)
  onDataPointChanged(evt: { dataKey: string; index: number; newValue: number }) {
    // Ensure the event corresponds to the currently selected parameter set
    if (evt.dataKey !== this.selectedParameterName()) return;

    const index = evt.index;
    const rowKey = this.getRowKey(this.selectedParameterName(), index);

    // Initialize original row tracking if this row wasn't previously edited via the table
    if (!this.editedRowsSet().has(rowKey)) {
      const originalData = { ...this.selectedDataSet()![index] };
      this.originalRowDataMap.update(map => new Map(map).set(rowKey, originalData));
      this.editedRowsSet.update(set => new Set(set).add(rowKey));
    }

    // Apply the new value to trackParameter1 (current editable value in plot)
    this.updateParameterEntry(index, entry => ({ ...entry, trackParameter1: evt.newValue }));

    // Refresh changes summary for this row
    const originalData = this.originalRowDataMap().get(rowKey);
    if (originalData) {
      const currentData = this.selectedDataSet()![index];
      const changes = this.generateChangesSummary(originalData, currentData);
      if (changes) {
        this.changesSummaryMap.update(map => new Map(map).set(rowKey, changes));
      } else {
        // If value reverted back to original, clear tracking
        this.clearStoredChangesForRow(rowKey);
      }
    }

    // Recalculate gradient values if elevation was edited, then rebuild chart
    // so the gradient series reflects the new values
    if (evt.dataKey === 'elevation_data') {
      this.recalculateElevationGradients();

      setTimeout(() => {
        try {
          const tp = this.trackPlotRef?.();
          if (tp?.rebuildChart) {
            tp.rebuildChart();
          }
        } catch (err) {
          console.debug('Failed to call rebuildChart after gradient recalculation', err);
        }
      }, 0);
    }

    // Mark full track as unsaved & revalidate
    this.markTrackAsManuallyChanged();
    this.validateAllRows();
  }
  // Plot datapoint add handler (emitted from <app-track-plot> context menu)
  onDataPointAdded(evt: { dataKey: string; position: number; value: number }) {
    console.log('onDataPointAdded called with:', evt);

    // Ensure the event corresponds to the currently selected parameter set
    if (evt.dataKey !== this.selectedParameterName()) {
      console.log('Data key mismatch:', evt.dataKey, 'vs', this.selectedParameterName());
      return;
    }

    const track = this.selectedTrack();
    if (!track?.parameters) {
      console.log('No track or parameters found');
      return;
    }

    const dataArray: TrackDataSet[] = track.parameters[this.selectedParameterName()];
    if (!Array.isArray(dataArray)) {
      console.log('Data array not found or not an array');
      return;
    }

    // Find the insertion index (sorted by position)
    let insertIndex = dataArray.findIndex(item => item.position > evt.position);
    if (insertIndex === -1) insertIndex = dataArray.length; // Insert at end

    // Create new data point with default values for trackParameter2 if needed
    const parameterInfo = this.trackService.parameterNameMappings[this.selectedParameterName()];
    const newDataPoint: TrackDataSet = {
      position: evt.position,
      trackParameter1: evt.value,
      trackParameter2: parameterInfo?.['trackParameter2'] ? 0 : undefined,
      comments: [],
      warning: false
    };

    console.log('Inserting new data point at index:', insertIndex, newDataPoint);

    // Perform immutable update: create new parameters object and replace array
    this.selectedTrack.update(trackVal => {
      if (!trackVal) return trackVal;
      const parameters = { ...trackVal.parameters };
      const oldArray = [...(parameters[this.selectedParameterName()] || [])];
      oldArray.splice(insertIndex, 0, newDataPoint);
      parameters[this.selectedParameterName()] = oldArray;
      return { ...trackVal, parameters };
    });

    // Update indices for any tracked changes (since we inserted a new row)
    this.updateStoredIndicesAfterInsertion(insertIndex);

    // Recalculate gradient values if elevation data was modified
    if (evt.dataKey === 'elevation_data') {
      this.recalculateElevationGradients();
    }

    // Mark track as unsaved and revalidate
    this.markTrackAsManuallyChanged();
    this.validateAllRows();

    // If the child component is available via template ref, request chart rebuild
    // Use setTimeout to ensure Angular change detection has completed and the track signal is updated
    setTimeout(() => {
      try {
        const tp = this.trackPlotRef?.();
        if (tp?.rebuildChart) {
          tp.rebuildChart();
        }
      } catch (err) {
        console.debug('Failed to call rebuildChart on trackPlotRef', err);
      }
    }, 0);

    console.log('Data point added successfully');
  }

  // Plot datapoint delete handler (emitted from <app-track-plot> when user presses Delete key)
  onDataPointDeleted(evt: { dataKey: string; index: number }) {
    console.log('onDataPointDeleted called with:', evt);

    // Ensure the event corresponds to the currently selected parameter set
    if (evt.dataKey !== this.selectedParameterName()) {
      console.log('Data key mismatch:', evt.dataKey, 'vs', this.selectedParameterName());
      return;
    }

    const track = this.selectedTrack();
    if (!track?.parameters) {
      console.log('No track or parameters found');
      return;
    }

    const dataArray: TrackDataSet[] = track.parameters[this.selectedParameterName()];
    if (!Array.isArray(dataArray)) {
      console.log('Data array not found or not an array');
      return;
    }

    if (evt.index < 0 || evt.index >= dataArray.length) {
      console.log('Invalid index:', evt.index);
      return;
    }

    console.log('Deleting data point at index:', evt.index);

    // Perform immutable update: create new parameters object and remove the element
    this.selectedTrack.update(trackVal => {
      if (!trackVal) return trackVal;
      const parameters = { ...trackVal.parameters };
      const oldArray = [...(parameters[this.selectedParameterName()] || [])];
      oldArray.splice(evt.index, 1); // Remove the element at the specified index
      parameters[this.selectedParameterName()] = oldArray;
      return { ...trackVal, parameters };
    });

    // Update indices for any tracked changes (since we deleted a row)
    this.updateStoredIndicesAfterDeletion(evt.index);

    // Recalculate gradient values if elevation data was modified
    if (evt.dataKey === 'elevation_data') {
      this.recalculateElevationGradients();
    }

    // Mark track as unsaved and revalidate
    this.markTrackAsManuallyChanged();
    this.validateAllRows();

    // Rebuild chart after change detection
    setTimeout(() => {
      try {
        const tp = this.trackPlotRef?.();
        if (tp?.rebuildChart) {
          tp.rebuildChart();
        }
      } catch (err) {
        console.debug('Failed to call rebuildChart on trackPlotRef', err);
      }
    }, 0);

    console.log('Data point deleted successfully');
  }

  // Helper method to update stored change indices after insertion
  private updateStoredIndicesAfterInsertion(insertedIndex: number): void {
    const parameterName = this.selectedParameterName();

    // Update originalRowDataMap keys
    const newOriginalMap = new Map();
    for (const [key, value] of this.originalRowDataMap()) {
      const [keyParam, keyIndex] = key.split('_');
      if (keyParam === parameterName) {
        const index = parseInt(keyIndex, 10);
        if (index >= insertedIndex) {
          const newKey = this.getRowKey(parameterName, index + 1);
          newOriginalMap.set(newKey, value);
        } else {
          newOriginalMap.set(key, value);
        }
      } else {
        newOriginalMap.set(key, value);
      }
    }
    this.originalRowDataMap.set(newOriginalMap);

    // Update editedRowsSet
    const newEditedSet = new Set<string>();
    for (const key of this.editedRowsSet()) {
      const [keyParam, keyIndex] = key.split('_');
      if (keyParam === parameterName) {
        const index = parseInt(keyIndex, 10);
        if (index >= insertedIndex) {
          newEditedSet.add(this.getRowKey(parameterName, index + 1));
        } else {
          newEditedSet.add(key);
        }
      } else {
        newEditedSet.add(key);
      }
    }
    this.editedRowsSet.set(newEditedSet);

    // Update changesSummaryMap
    const newChangesMap = new Map();
    for (const [key, value] of this.changesSummaryMap()) {
      const [keyParam, keyIndex] = key.split('_');
      if (keyParam === parameterName) {
        const index = parseInt(keyIndex, 10);
        if (index >= insertedIndex) {
          const newKey = this.getRowKey(parameterName, index + 1);
          newChangesMap.set(newKey, value);
        } else {
          newChangesMap.set(key, value);
        }
      } else {
        newChangesMap.set(key, value);
      }
    }
    this.changesSummaryMap.set(newChangesMap);
  }

  // Helper method to update stored change indices after deletion
  private updateStoredIndicesAfterDeletion(deletedIndex: number): void {
    const parameterName = this.selectedParameterName();

    // Update originalRowDataMap keys
    const newOriginalMap = new Map();
    for (const [key, value] of this.originalRowDataMap()) {
      const [keyParam, keyIndex] = key.split('_');
      if (keyParam === parameterName) {
        const index = parseInt(keyIndex, 10);
        if (index === deletedIndex) {
          // Skip the deleted row
          continue;
        } else if (index > deletedIndex) {
          const newKey = this.getRowKey(parameterName, index - 1);
          newOriginalMap.set(newKey, value);
        } else {
          newOriginalMap.set(key, value);
        }
      } else {
        newOriginalMap.set(key, value);
      }
    }
    this.originalRowDataMap.set(newOriginalMap);

    // Update editedRowsSet
    const newEditedSet = new Set<string>();
    for (const key of this.editedRowsSet()) {
      const [keyParam, keyIndex] = key.split('_');
      if (keyParam === parameterName) {
        const index = parseInt(keyIndex, 10);
        if (index === deletedIndex) {
          // Skip the deleted row
          continue;
        } else if (index > deletedIndex) {
          newEditedSet.add(this.getRowKey(parameterName, index - 1));
        } else {
          newEditedSet.add(key);
        }
      } else {
        newEditedSet.add(key);
      }
    }
    this.editedRowsSet.set(newEditedSet);

    // Update changesSummaryMap
    const newChangesMap = new Map();
    for (const [key, value] of this.changesSummaryMap()) {
      const [keyParam, keyIndex] = key.split('_');
      if (keyParam === parameterName) {
        const index = parseInt(keyIndex, 10);
        if (index === deletedIndex) {
          // Skip the deleted row
          continue;
        } else if (index > deletedIndex) {
          newChangesMap.set(this.getRowKey(parameterName, index - 1), value);
        } else {
          newChangesMap.set(key, value);
        }
      } else {
        newChangesMap.set(key, value);
      }
    }
    this.changesSummaryMap.set(newChangesMap);
  }

  // Helper method to generate changes summary
  private generateChangesSummary(
    originalData: TrackDataSet,
    currentData: TrackDataSet
  ): string | null {
    const changes: string[] = [];
    const displayNames = this.trackService.parameterNameMappings[this.selectedParameterName()];

    // Check all trackParameter keys for changes
    this.trackParameterKeys().forEach(key => {
      const originalValue = originalData[key];
      const currentValue = currentData[key];

      if (originalValue !== currentValue) {
        const displayName = displayNames[key]?.name || key;
        const originalDisplay = this.formatValueForDisplay(originalValue);
        const currentDisplay = this.formatValueForDisplay(currentValue);
        changes.push(`${displayName}: ${originalDisplay} → ${currentDisplay}`);
      }
    });

    return changes.length > 0 ? changes.join('\n') : null;
  }

  // Helper method to format values for display
  private formatValueForDisplay(value: any): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return String(value);
  } // Helper method to clear stored changes for a specific row
  private clearStoredChangesForRow(rowKey: string) {
    this.originalRowDataMap.update(map => {
      const newMap = new Map(map);
      newMap.delete(rowKey);
      return newMap;
    });
    this.changesSummaryMap.update(map => {
      const newMap = new Map(map);
      newMap.delete(rowKey);
      return newMap;
    });
    this.editedRowsSet.update(set => {
      const newSet = new Set(set);
      newSet.delete(rowKey);
      return newSet;
    });
  }
  // Helper method to clear all stored changes (used for operations like reverse)
  private clearAllStoredChanges() {
    this.originalRowDataMap.set(new Map());
    this.changesSummaryMap.set(new Map());
    this.editedRowsSet.set(new Set());
  }

  /**
   * Recalculates gradient (trackParameter2) for all elevation_data entries
   * based on height_above_sea_level (trackParameter1) and position.
   *
   * Formula: gradient[i] = (elevation[i+1] - elevation[i]) / (position[i+1] - position[i]) * 1000  [‰]
   * The last point gets gradient = 0 (no next point to compute a forward difference).
   *
   * This must be called whenever elevation_data is modified (edit, add, or delete).
   */
  private recalculateElevationGradients(): void {
    const track = this.selectedTrack();
    if (!track?.parameters?.['elevation_data']) return;

    const elevationData: TrackDataSet[] = track.parameters['elevation_data'];
    if (elevationData.length === 0) return;

    const updatedData = elevationData.map((entry, i) => {
      let gradient = 0;
      if (i < elevationData.length - 1) {
        const deltaElevation =
          Number(elevationData[i + 1].trackParameter1) - Number(entry.trackParameter1);
        const deltaPosition = Number(elevationData[i + 1].position) - Number(entry.position);
        gradient =
          deltaPosition !== 0 ? Math.round((deltaElevation / deltaPosition) * 1000 * 100) / 100 : 0;
      }
      return { ...entry, trackParameter2: gradient };
    });

    this.selectedTrack.update(t => {
      if (!t) return t;
      const parameters = { ...t.parameters, elevation_data: updatedData };
      return { ...t, parameters };
    });
  }
}
